<script>
  import backend from "../server";
  import { onMount } from "svelte";
  import Loading from "../lib/components/Loading.svelte";
  import { ICONS } from "../js/icons";
  import Select from "svelte-select";

  let activeTab = "usuarios";

  // ============= USUARIOS =============
  let users = [];
  let usersLoading = false;
  let showUserForm = false;
  let editingUser = null;
  let userForm = { username_origin: "", password: "", firstname: "", clientcode: "" };
  let groupsCache = [];
  let groupItems = [];
  let selectedGroupValue = null;
  let showPasswordModal = false;
  let passwordUser = null;
  let newPassword = '';
  let showUserPassword = false;
  let showNewPassword = false;
  let filterUsername = "";
  let filterGroup = "";

  $: if (!showUserForm) showUserPassword = false;
  $: if (!showPasswordModal) showNewPassword = false;

  $: filteredUsers = users.filter(u => {
    const matchesUsername = !filterUsername ||
      (u.username_origin || u.username || "").toLowerCase().includes(filterUsername.toLowerCase());
    const matchesGroup = !filterGroup || (u.clientcode || "") === filterGroup;
    return matchesUsername && matchesGroup;
  });

  async function loadUsers() {
    usersLoading = true;
    try {
      const res = await backend.management.list();
      users = res.data || [];
    } catch (e) {
      console.error(e);
      users = [];
    } finally {
      usersLoading = false;
    }
  }

  async function loadGroups() {
    try {
      const res = await backend.credentials.list("client_group.");
      const map = res.data || {};
      const codes = new Set();
      for (const key of Object.keys(map)) {
        const m = key.match(/^client_group\.([^.]+)$/);
        if (m) codes.add(m[1]);
      }
      groupsCache = Array.from(codes).sort();
      groupItems = groupsCache.map(g => ({ value: g, label: g }));
    } catch (e) {
      groupsCache = [];
      groupItems = [];
    }
  }

  function openNewUser() {
    editingUser = null;
    userForm = { username_origin: "", password: "", firstname: "", clientcode: groupsCache[0] || "" };
    selectedGroupValue = userForm.clientcode ? { value: userForm.clientcode, label: userForm.clientcode } : null;
    showUserForm = true;
  }

  function openEditUser(u) {
    editingUser = u;
    userForm = { username_origin: u.username_origin || "", password: "", firstname: u.firstname || "", clientcode: u.clientcode || "" };
    selectedGroupValue = u.clientcode ? { value: u.clientcode, label: u.clientcode } : null;
    showUserForm = true;
  }

  async function saveUser() {
    const cleanUsername = (userForm.username_origin || '').replace(/ /g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
    if (!cleanUsername) return alert("Username requerido");
    if (!editingUser) {
      if (!userForm.password) return alert("Password requerido");
    }
    const payload = {
      address: "GESTOR_BONOS",
      username_origin: cleanUsername,
      firstname: userForm.firstname,
      clientcode: selectedGroupValue ? selectedGroupValue.value : "",
      status: 1,
      role: null,
    };
    if (!editingUser && userForm.password) payload.pass = userForm.password;
    if (editingUser) payload.id = editingUser.id;
    try {
      await backend.adminusers.save(payload);
      showUserForm = false;
      await loadUsers();
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Error al guardar el usuario";
      alert(msg);
    }
  }

  function openChangePassword(u) {
    passwordUser = u;
    newPassword = '';
    showPasswordModal = true;
  }

  async function saveNewPassword() {
    if (!newPassword) return alert("Password requerido");
    if (!confirm(`¿Estás seguro de cambiar la contraseña de ${passwordUser.username_origin || passwordUser.username}?`)) return;
    try {
      await backend.adminusers.save({ id: passwordUser.id, address: "GESTOR_BONOS", pass: newPassword });
      showPasswordModal = false;
      passwordUser = null;
      newPassword = '';
    } catch (e) {
      alert("Error: " + (e.response?.data?.message || e.message));
    }
  }

  async function deleteUser(u) {
    if (!confirm(`¿Eliminar usuario ${u.username_origin || u.username}?`)) return;
    try {
      await backend.adminusers.remove(u.id);
      await loadUsers();
    } catch (e) {
      alert("Error: " + (e.response?.data?.message || e.message));
    }
  }

  // ============= GRUPOS =============
  let groups = [];
  let groupsLoading = false;
  let showGroupForm = false;
  let editingGroup = null;
  let groupForm = { rawName: "", clients: "", views: "" };

  let allClients = [];
  let clientNames = {};
  let selectedClients = [];
  let clientSearch = "";

  let viewAmigo = false;
  let viewFirst = false;
  let viewFirstV2 = false;
  let viewPragmatic = false;
  let viewClients = false;

  const VIEW_LABELS = { amigo: 'AmigoGaming', first: 'First', firstV2: 'First V2', pragmatic: 'Pragmatic', clients: 'Banners' };

  function viewList(str) {
    return (str || '').split(',').map(s => s.trim()).filter(Boolean).map(s => VIEW_LABELS[s] || s);
  }

  function clientList(str) {
    return (str || '').split(',').map(s => s.trim()).filter(Boolean);
  }

  function normalizeGroupName(name) {
    return name
      .replace(/[^a-zA-Z0-9 _-]/g, '')
      .replace(/[ -]/g, '_')
      .replace(/_+/g, '_')
      .toUpperCase();
  }

  $: groupCode = normalizeGroupName(groupForm.rawName);

  async function loadAllClients() {
    try {
      const res = await backend.client.list({ xpage: 500, page: 1 });
      allClients = (res.data?.list || []).filter(c => c.code);
      clientNames = {};
      for (const c of allClients) clientNames[c.code] = c.namesite || c.businessname || c.code;
    } catch (e) {
      allClients = [];
    }
  }

  function clientLabel(code) {
    const name = clientNames[code];
    return name ? `${code} - ${name}` : code;
  }

  function toggleClient(code, checked) {
    if (checked) {
      if (!selectedClients.includes(code)) selectedClients = [...selectedClients, code];
    } else {
      selectedClients = selectedClients.filter(c => c !== code);
    }
  }

  function removeClient(code) {
    selectedClients = selectedClients.filter(c => c !== code);
  }

  $: filteredClientList = clientSearch
    ? allClients.filter(c => {
        const term = clientSearch.toLowerCase();
        return (c.code || "").toLowerCase().includes(term) ||
               (c.namesite || c.businessname || "").toLowerCase().includes(term);
      })
    : allClients;

  function parseViewsToToggles(viewsStr) {
    const parts = (viewsStr || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    viewAmigo = parts.includes('amigo');
    viewFirst = parts.includes('first');
    viewFirstV2 = parts.includes('firstv2');
    viewPragmatic = parts.includes('pragmatic');
    viewClients = parts.includes('clients');
  }

  function togglesToViews() {
    return [viewAmigo && 'amigo', viewFirst && 'first', viewFirstV2 && 'firstV2', viewPragmatic && 'pragmatic', viewClients && 'clients'].filter(Boolean).join(',');
  }

  async function loadGroupsFull() {
    groupsLoading = true;
    try {
      const res = await backend.credentials.list("client_group.");
      const map = res.data || {};
      const byCode = {};
      for (const [key, val] of Object.entries(map)) {
        const m = key.match(/^client_group\.([^.]+)(?:\.(.+))?$/);
        if (m) {
          const code = m[1];
          const sub = m[2] || "clients";
          if (!byCode[code]) byCode[code] = { code, clients: "", views: "" };
          if (sub === "views") byCode[code].views = val;
          else byCode[code].clients = val;
        }
      }
      groups = Object.values(byCode).sort((a, b) => a.code.localeCompare(b.code));
    } catch (e) {
      console.error(e);
      groups = [];
    } finally {
      groupsLoading = false;
    }
  }

  function openNewGroup() {
    editingGroup = null;
    groupForm = { rawName: "", clients: "", views: "" };
    selectedClients = [];
    clientSearch = "";
    parseViewsToToggles('');
    showGroupForm = true;
  }

  function openEditGroup(g) {
    editingGroup = g;
    groupForm = { rawName: g.code, clients: g.clients, views: g.views };
    selectedClients = (g.clients || '').split(',').map(s => s.trim()).filter(Boolean);
    clientSearch = "";
    parseViewsToToggles(g.views);
    showGroupForm = true;
  }

  async function saveGroup() {
    const code = editingGroup ? editingGroup.code : groupCode;
    if (!code || !/^[A-Z0-9_]+$/.test(code)) return alert("Nombre inválido (solo mayúsculas, números, _)");
    if (!editingGroup) {
      const existing = await backend.credentials.list("client_group.");
      if (existing.data?.[`client_group.${code}`]) return alert("El nombre de grupo ya existe");
    }
    try {
      await backend.credentials.save({ key: `client_group.${code}`, value: selectedClients.join(',') });
      await backend.credentials.save({ key: `client_group.${code}.views`, value: togglesToViews() });
      showGroupForm = false;
      await loadGroupsFull();
      await loadGroups();
    } catch (e) {
      alert("Error al guardar: " + (e.response?.data?.message || e.message));
    }
  }

  async function deleteGroup(g) {
    if (!confirm(`¿Eliminar grupo ${g.code}?`)) return;
    try {
      await backend.credentials.remove(`client_group.${g.code}`);
      await backend.credentials.remove(`client_group.${g.code}.views`);
      await loadGroupsFull();
      await loadGroups();
    } catch (e) {
      alert("Error: " + (e.response?.data?.message || e.message));
    }
  }

  onMount(async () => {
    await Promise.all([loadUsers(), loadGroupsFull(), loadGroups(), loadAllClients()]);
  });
</script>

<div class="bonus-management-container">
  <div class="tab-bar mb-3 mt-2">
    <button class="tab-btn" class:active={activeTab==='usuarios'} on:click={() => activeTab='usuarios'}>
      {@html ICONS.user} Usuarios
    </button>
    <button class="tab-btn" class:active={activeTab==='grupos'} on:click={() => activeTab='grupos'}>
      {@html ICONS.persons} Grupos
    </button>
  </div>

  {#if activeTab === 'usuarios'}
    <div class="card-section">
      <div class="toolbar toolbar-responsive">
        <div class="filters">
          <input class="form-control form-control-sm" bind:value={filterUsername} placeholder="Filtrar username..." />
          <select class="form-select form-select-sm" bind:value={filterGroup}>
            <option value="">Todos los grupos</option>
            {#each groupsCache as g}
              <option value={g}>{g}</option>
            {/each}
          </select>
        </div>
        <button class="btn btn-primary btn-sm" on:click={openNewUser}>{@html ICONS.addCircle} Nuevo Usuario</button>
      </div>
      <div class="container-table mt-2">
        {#if usersLoading}
          <Loading loading />
        {:else}
          <table class="table table-striped table-sm">
            <thead class="table-dark">
              <tr>
                <th>#</th>
                <th>Username</th>
                <th>Nombre</th>
                <th>Grupo</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each filteredUsers as u, i}
                <tr>
                  <td>{i + 1}</td>
                  <td>{u.username_origin || u.username}</td>
                  <td>{u.firstname || '-'}</td>
                  <td>{u.clientcode || '-'}</td>
                  <td style="color:{u.status == 0 ? 'red' : 'green'}">{u.status == 0 ? 'Inactivo' : 'Activo'}</td>
                  <td class="center">
                    <div class="action-btns">
                      <button class="btn btn-info btn-sm" on:click={() => openEditUser(u)} title="Editar">{@html ICONS.edit}</button>
                      <button class="btn btn-warning btn-sm" on:click={() => openChangePassword(u)} title="Cambiar contraseña">{@html ICONS.lock}</button>
                      <button class="btn btn-danger btn-sm" on:click={() => deleteUser(u)} title="Eliminar">{@html ICONS.delete}</button>
                    </div>
                  </td>
                </tr>
              {:else}
                <tr><td colspan="6" class="text-center">
                  {users.length === 0 ? "No hay usuarios para la gestión de BONOS" : "Ningún usuario coincide con los filtros"}
                </td></tr>
              {/each}
            </tbody>
          </table>
        {/if}
      </div>
    </div>

    {#if showUserForm}
      <div class="modal-backdrop" on:click={() => showUserForm=false}></div>
      <div class="modal-form modal-form-wide">
        <h5 class="modal-title">{editingUser ? 'Editar' : 'Nuevo'} Usuario</h5>
        <div class="form-section">
          <label class="form-label">Username</label>
          <input class="form-control" bind:value={userForm.username_origin} placeholder="ej: jperez" disabled={editingUser} />
        </div>
        <div class="form-section">
          <label class="form-label">Nombre</label>
          <input class="form-control" bind:value={userForm.firstname} placeholder="ej: Juan" />
        </div>
        <div class="form-section">
          <label class="form-label">Grupo</label>
          <Select
            items={groupItems}
            bind:value={selectedGroupValue}
            placeholder="Seleccionar grupo..."
            optionIdentifier="value"
            labelIdentifier="label"
            isSearchable={true}
          />
        </div>

        {#if !editingUser}
          <div class="form-section">
            <label class="form-label">Password</label>
            <div class="input-group password-input">
              {#if showUserPassword}
                <input class="form-control" type="text" bind:value={userForm.password} on:input={(e) => userForm.password = e.target.value.replace(/\s/g, '')} on:keydown={(e) => e.key === ' ' && e.preventDefault()} placeholder="••••••" />
              {:else}
                <input class="form-control" type="password" bind:value={userForm.password} on:input={(e) => userForm.password = e.target.value.replace(/\s/g, '')} on:keydown={(e) => e.key === ' ' && e.preventDefault()} placeholder="••••••" />
              {/if}
              <button class="btn btn-outline-secondary" type="button" on:click={() => showUserPassword = !showUserPassword}>
                {@html showUserPassword ? ICONS.eyeOff : ICONS.eye}
              </button>
            </div>
          </div>
        {/if}

        <div class="d-flex gap-2 mt-3 justify-content-end border-top pt-3">
          <button class="btn btn-primary" on:click={saveUser}>Guardar</button>
          <button class="btn btn-secondary" on:click={() => showUserForm=false}>Cancelar</button>
        </div>
      </div>
    {/if}

    {#if showPasswordModal}
      <div class="modal-backdrop" on:click={() => showPasswordModal=false}></div>
      <div class="modal-form modal-form-wide">
        <h5 class="modal-title">Cambiar contraseña</h5>
        <p class="text-muted small">{passwordUser?.username_origin || passwordUser?.username}</p>
        <div class="form-section">
          <label class="form-label">Nueva contraseña</label>
          <div class="input-group password-input">
            {#if showNewPassword}
              <input class="form-control" type="text" bind:value={newPassword} on:input={(e) => newPassword = e.target.value.replace(/\s/g, '')} on:keydown={(e) => e.key === ' ' && e.preventDefault()} placeholder="••••••" />
            {:else}
              <input class="form-control" type="password" bind:value={newPassword} on:input={(e) => newPassword = e.target.value.replace(/\s/g, '')} on:keydown={(e) => e.key === ' ' && e.preventDefault()} placeholder="••••••" />
            {/if}
            <button class="btn btn-outline-secondary" type="button" on:click={() => showNewPassword = !showNewPassword}>
              {@html showNewPassword ? ICONS.eyeOff : ICONS.eye}
            </button>
          </div>
        </div>
        <div class="d-flex gap-2 mt-3 justify-content-end border-top pt-3">
          <button class="btn btn-primary" on:click={saveNewPassword}>Guardar</button>
          <button class="btn btn-secondary" on:click={() => showPasswordModal=false}>Cancelar</button>
        </div>
      </div>
    {/if}

  {:else if activeTab === 'grupos'}
    <div class="card-section">
      <div class="toolbar toolbar-responsive">
        <div class="filters"></div>
        <button class="btn btn-primary btn-sm" on:click={openNewGroup}>{@html ICONS.addCircle} Nuevo Grupo</button>
      </div>
      <div class="container-table mt-2">
      {#if groupsLoading}
        <Loading loading />
      {:else}
        <table class="table table-striped table-sm">
          <thead class="table-dark">
            <tr>
              <th class="col-group-name">Nombre</th>
              <th>Clientes</th>
              <th>Vistas</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {#each groups as g}
              <tr>
                <td class="col-group-name"><strong>{g.code}</strong></td>
                <td>
                  {#each clientList(g.clients) as c}
                    <span class="pill pill-client" title={clientLabel(c)}>{c}</span>
                  {:else}
                    <span class="text-muted">-</span>
                  {/each}
                </td>
                <td>
                  {#each viewList(g.views) as v}
                    <span class="pill pill-view">{v}</span>
                  {:else}
                    <span class="text-muted">-</span>
                  {/each}
                </td>
                <td class="center">
                  <div class="action-btns">
                    <button class="btn btn-info btn-sm" on:click={() => openEditGroup(g)} title="Editar">{@html ICONS.edit}</button>
                    <button class="btn btn-danger btn-sm" on:click={() => deleteGroup(g)} title="Eliminar">{@html ICONS.delete}</button>
                  </div>
                </td>
              </tr>
            {:else}
              <tr><td colspan="4" class="text-center">No hay grupos</td></tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>
    </div>

    {#if showGroupForm}
      <div class="modal-backdrop" on:click={() => showGroupForm=false}></div>
      <div class="modal-form modal-form-wide">
        <h5 class="modal-title">{editingGroup ? 'Editar' : 'Nuevo'} Grupo</h5>

        <div class="form-section">
          <label class="form-label">Nombre</label>
          {#if !editingGroup}
            <input class="form-control" bind:value={groupForm.rawName} placeholder="ej: Usuarios Reportes" />
            <div class="preview mt-1">
              {#if groupCode}
                <span class="badge badge-preview">{groupCode}</span>
              {:else}
                <span class="text-muted small">Vista previa del código</span>
              {/if}
            </div>
          {:else}
            <div class="preview">
              <span class="badge badge-preview">{groupForm.rawName}</span>
            </div>
          {/if}
        </div>

        <div class="form-section">
          <label class="form-label">Clientes</label>
          <div class="chips-wrap mb-2">
            {#each selectedClients as code}
              <span class="chip chip-client" title="Quitar {clientLabel(code)}">
                <span class="chip-code">{code}</span>
                <span class="chip-name">{clientNames[code] || ''}</span>
                <button class="chip-remove" on:click={() => removeClient(code)}>&times;</button>
              </span>
            {:else}
              <span class="text-muted small">No hay clientes seleccionados</span>
            {/each}
          </div>
          <input class="form-control form-control-sm mb-2" bind:value={clientSearch} placeholder="Buscar cliente..." />
          <div class="client-list">
            {#each filteredClientList as c}
              <label class="client-item">
                <input type="checkbox" checked={selectedClients.includes(c.code)} on:change={(e) => toggleClient(c.code, e.target.checked)} />
                <span class="client-item-code">{c.code}</span>
                <span class="client-item-name">{c.namesite || c.businessname || c.code}</span>
              </label>
            {:else}
              <span class="text-muted small">No se encontraron clientes</span>
            {/each}
          </div>
        </div>

        <div class="form-section">
          <label class="form-label">Vistas</label>
          <div class="views-row">
            <label class="view-chip" class:active={viewAmigo}>
              <input type="checkbox" bind:checked={viewAmigo} />
              <span>AmigoGaming</span>
            </label>
            <label class="view-chip" class:active={viewFirst}>
              <input type="checkbox" bind:checked={viewFirst} />
              <span>First</span>
            </label>
            <label class="view-chip" class:active={viewFirstV2}>
              <input type="checkbox" bind:checked={viewFirstV2} />
              <span>First V2</span>
            </label>
            <label class="view-chip" class:active={viewPragmatic}>
              <input type="checkbox" bind:checked={viewPragmatic} />
              <span>Pragmatic</span>
            </label>
            <label class="view-chip" class:active={viewClients}>
              <input type="checkbox" bind:checked={viewClients} />
              <span>Banners</span>
            </label>
          </div>
        </div>

        <div class="d-flex gap-2 mt-3 justify-content-end border-top pt-3">
          <button class="btn btn-primary" on:click={saveGroup}>Guardar</button>
          <button class="btn btn-secondary" on:click={() => showGroupForm=false}>Cancelar</button>
        </div>
      </div>
    {/if}
  {/if}
</div>

<style>
  .bonus-management-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 1.5rem;
  }

  .card-section {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    padding: 1rem 1.25rem 1.25rem;
    box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  }

  .tab-bar {
    display: flex;
    gap: 0;
    border-bottom: 2px solid #dee2e6;
  }
  .tab-btn {
    padding: 0.5rem 1.5rem;
    border: none;
    background: none;
    cursor: pointer;
    font-weight: 500;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    color: #6c757d;
  }
  .tab-btn.active {
    color: #FF0090;
    border-bottom-color: #FF0090;
  }
  .toolbar {
    display: flex;
    justify-content: flex-end;
    gap: 0.3rem;
    align-items: center;
  }
  .toolbar-responsive {
    justify-content: space-between;
  }
  .toolbar-responsive .filters {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
  .toolbar-responsive .filters input,
  .toolbar-responsive .filters select,
  .toolbar-responsive .btn {
    height: 31px;
    min-width: 180px;
  }
  .toolbar-responsive .btn {
    min-width: auto;
  }

  :global(.table tbody tr) {
    transition: box-shadow 0.2s ease;
  }
  :global(.table tbody tr:hover) {
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.08);
    background-color: rgba(255, 0, 144, 0.04);
  }
  .center { text-align: center; }
  .action-btns {
    display: flex;
    gap: 0.3rem;
    justify-content: center;
    flex-wrap: nowrap;
  }
  .col-group-name {
    min-width: 160px;
    padding-right: 1.5rem;
  }
  .modal-backdrop {
    position: fixed;
    top: 0; left: 0; width: 100vw; height: 100vh;
    background: rgba(0,0,0,0.4);
    z-index: 1040;
  }
  .modal-form {
    position: fixed;
    top: 50%; left: 50%; transform: translate(-50%, -50%);
    background: #fff;
    padding: 1.5rem;
    border-radius: 0.5rem;
    z-index: 1050;
    min-width: 360px;
    max-height: 95vh;
    box-shadow: 0 4px 20px rgba(0,0,0,0.2);
  }
  .modal-form-wide {
    width: 620px;
    max-width: 92vw;
  }
  .modal-title {
    margin: 0 0 1rem;
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.5rem;
  }
  .form-section {
    margin-bottom: 1rem;
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
    padding: 1rem;
    background: #fafbfc;
  }
  .form-section .form-label,
  .form-label {
    font-weight: 700;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    color: #333;
    display: block;
  }
  .modal-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    border-top: 1px solid #eee;
    padding-top: 1rem;
    margin-top: 0.5rem;
  }
  .preview { min-height: 1.5rem; }
  .badge-preview {
    background: #FF0090;
    color: #fff;
    padding: 0.2rem 0.6rem;
    border-radius: 0.25rem;
    font-size: 0.85rem;
    font-family: monospace;
  }
  .flex-grow-1 { flex: 1; }

  .pill {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.8rem;
    font-weight: 500;
    margin: 0.1rem 0.15rem;
  }
  .pill-client {
    background: #e8f4fd;
    color: #0d6efd;
    border: 1px solid #b8daff;
  }
  .pill-view {
    background: #f0fdf4;
    color: #198754;
    border: 1px solid #b8e6c9;
  }

  .chips-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    min-height: 2rem;
    max-height: 120px;
    overflow-y: auto;
    padding: 0.35rem 0;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 0.35rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.8rem;
    transition: box-shadow 0.15s;
  }
  .chip:hover {
    box-shadow: 0 1px 4px rgba(0,0,0,0.1);
    border-color: #adb5bd;
  }
  .chip-code {
    font-weight: 600;
    color: #0d6efd;
  }
  .chip-name {
    color: #6c757d;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chip-remove {
    background: none;
    border: none;
    font-size: 1.1rem;
    line-height: 1;
    padding: 0 0 0 0.2rem;
    cursor: pointer;
    color: #dc3545;
  }
  .chip-remove:hover { color: #a71d2a; }

  .client-list {
    width: 100%;
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #ced4da;
    border-radius: 0.375rem;
    padding: 0.35rem;
    background: #fff;
  }
  .client-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.5rem;
    border-radius: 0.25rem;
    cursor: pointer;
    font-size: 0.85rem;
    transition: background-color 0.1s;
  }
  .client-item:hover {
    background-color: #f8f9fa;
  }
  .client-item input[type="checkbox"] {
    margin: 0;
    flex-shrink: 0;
  }
  .client-item-code {
    font-weight: 600;
    color: #0d6efd;
    min-width: 45px;
  }
  .client-item-name {
    color: #495057;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .password-input {
    display: flex;
  }
  .password-input input {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .password-input .btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
  }

  .views-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .view-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    border: 1px solid #ced4da;
    border-radius: 0.35rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.85rem;
    cursor: pointer;
    background: #f8f9fa;
    color: #495057;
    transition: all 0.15s ease;
    user-select: none;
  }
  .view-chip input {
    display: none;
  }
  .view-chip.active {
    background: #FF0090;
    border-color: #FF0090;
    color: #fff;
  }
  .view-chip:hover {
    border-color: #FF0090;
    color: #FF0090;
  }
  .view-chip.active:hover {
    color: #fff;
  }
  :global(.svelte-select) {
    width: 100% !important;
    border: 1px solid #ced4da !important;
    border-radius: .375rem !important;
    min-height: 38px !important;
    --font-size: 0.9rem;
  }
  :global(.svelte-select .input-container) {
    padding: .375rem .75rem !important;
  }
  :global(.svelte-select .input-container input) {
    font-size: 0.9rem !important;
  }
  :global(.svelte-select.focused) {
    border-color: #86b7fe !important;
    box-shadow: 0 0 0 .25rem rgba(13,110,253,.25) !important;
  }
  :global(.svelte-select .list-container) {
    z-index: 1060 !important;
    width: 100% !important;
  }
  :global(.svelte-select .list-container .list-item) {
    font-size: 0.9rem !important;
    padding: .375rem .75rem !important;
  }
</style>
